import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { WizardV2StateModel } from '../models/state/wizard-state.model';
import { LoadJobFileAction, ResetWizardV2State, DeleteShipmentOrderJobFile, DeleteAllShipmentOrdersJobFile, UploadWizardAttachments, RemoveWizardAttachment, InviteCollaborators, RemoveCollaborator, SetRouteOptions, PatchShipmentOrderSpecsAction } from './wizard.actions';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { JobFile } from '@core/models/job-file';
import { SetNotificationAction } from '@core/store/notification/notification.actions';
import { AlertDialogType } from '@shared/models';
import { JobFileService } from '@core/services/job-file.service';
import { SetSpinner } from '@core/store/shared.actions';
import { JobPlaningService } from '../services/job-planing.service';
import { EMPTY } from 'rxjs';

export const getInitialState = (): WizardV2StateModel => ({
  jobFile: null,
  routeOptions: null
});

export const WizardV2StateKey = 'wizardV2';

@State<WizardV2StateModel>({
  name: 'wizardV2',
  defaults: getInitialState()
})
@Injectable()
export class WizardV2State {

  constructor(
    public store$: Store,
    private jobFileService: JobFileService,
    private jobPlaningService: JobPlaningService
  ) {}

  @Selector()
  static getJobFile(state: WizardV2StateModel): JobFile {
    return state.jobFile;
  }

  @Selector()
  static getRouteOptions(state: WizardV2StateModel) {
    return state.routeOptions;
  }

  @Action(LoadJobFileAction)
  loadJobFile({ getState, patchState, dispatch }: StateContext<WizardV2StateModel>, { jobRefId }: LoadJobFileAction) {
    const { user: { orgId } } = this.store$.selectSnapshot(state => state.auth);

    return this.jobFileService.getJobFileByOrgIdAndJobRefId(orgId, jobRefId)
      .pipe(
        map((jobFile: JobFile) => {
          // The job file endpoint does not return shipmentOrderSpecs; they are loaded separately
          // by the order list. Carry over any specs already held for the same job so a job file
          // reload that resolves after the specs request does not wipe the orders from the UI.
          const existing = getState().jobFile;
          const carriedSpecs = jobFile?.shipmentOrderSpecs !== undefined
            ? jobFile.shipmentOrderSpecs
            : (existing?.jobRefId === jobFile?.jobRefId ? existing?.shipmentOrderSpecs : undefined);

          return patchState({
            jobFile: carriedSpecs !== undefined ? { ...jobFile, shipmentOrderSpecs: carriedSpecs } : jobFile
          });
        }),
        catchError(err => dispatch(new SetNotificationAction('Error loading job', AlertDialogType.ERROR)))
      );
  }

  @Action(ResetWizardV2State)
  resetState({ patchState }: StateContext<WizardV2StateModel>) {
    return patchState(getInitialState());
  }

  @Action(PatchShipmentOrderSpecsAction)
  patchShipmentOrderSpecs({ getState, patchState }: StateContext<WizardV2StateModel>, { shipmentOrderSpecs }: PatchShipmentOrderSpecsAction) {
    patchState({ jobFile: { ...getState().jobFile, shipmentOrderSpecs } });
  }

  @Action(DeleteShipmentOrderJobFile)
  deleteShipmentOrderSpecFromJobFile({ getState, patchState, dispatch }: StateContext<WizardV2StateModel>, { shipmentOrderId, deleteFromOrderManager }: DeleteShipmentOrderJobFile) {
    const { jobFile } = getState();
    return this.jobFileService.deleteShipmentOrderSpecFromJobFile(jobFile?.jobRefId, shipmentOrderId, deleteFromOrderManager)
      .pipe(
        tap(() => {
          // The list emits `order.id ?? order.orderId` as the identifier, so match on either
          // to keep the local state in sync with the row the user removed.
          const updatedSpecs = (getState().jobFile?.shipmentOrderSpecs || []).filter((s: any) => s.id !== shipmentOrderId && s.orderId !== shipmentOrderId);
          patchState({ jobFile: { ...getState().jobFile, shipmentOrderSpecs: updatedSpecs } });
        }),
        mergeMap(() => dispatch(new SetNotificationAction('Successfully deleted shipment order', AlertDialogType.SUCCESS))),
        catchError(() => dispatch(new SetNotificationAction('Error deleting shipment order', AlertDialogType.ERROR)))
      );
  }

  @Action(DeleteAllShipmentOrdersJobFile)
  deleteAllShipmentOrderSpecsFromJobFile({ getState, dispatch }: StateContext<WizardV2StateModel>, { deleteFromOrderManager }: DeleteAllShipmentOrdersJobFile) {
    const { jobFile } = getState();
    return this.jobFileService.deleteAllShipmentOrderSpecsFromJobFile(jobFile?.jobRefId, deleteFromOrderManager)
      .pipe(
        mergeMap(() => dispatch([new PatchShipmentOrderSpecsAction([]), new SetSpinner(false)])),
        catchError(() => dispatch([new SetNotificationAction('Error deleting all shipment orders', AlertDialogType.ERROR), new SetSpinner(false)]))
      );
  }

  @Action(UploadWizardAttachments)
  uploadWizardAttachments({getState, dispatch}: StateContext<WizardV2StateModel>, {files, path}: UploadWizardAttachments) {
    const {jobFile: {orgId, jobRefId}} = getState();
    const url = this.jobFileService.modifyUrlReplacingParameter(path, orgId, jobRefId);
    return this.jobFileService.uploadWizardAttachments(files, url)
      .pipe(
        mergeMap(() => dispatch(new SetNotificationAction('Successfully file uploaded', AlertDialogType.SUCCESS))),
        catchError(error => dispatch(new SetNotificationAction('Error Uploading File', AlertDialogType.ERROR)))
      );
  }

  @Action(RemoveWizardAttachment)
  removeWizardAttachments({getState, dispatch}: StateContext<WizardV2StateModel>, {attachmentId}: RemoveWizardAttachment) {
    const {jobFile: {orgId, jobRefId}} = getState();
    return this.jobFileService.removeWizardAttachment(orgId, jobRefId, attachmentId)
      .pipe(
        mergeMap(() => dispatch(new SetNotificationAction('Successfully remove attachment', AlertDialogType.SUCCESS))),
        catchError(error => dispatch(new SetNotificationAction('Error removing attachment', AlertDialogType.ERROR)))
      );
  }

  @Action(InviteCollaborators)
  inviteCollaborators(ctx: StateContext<WizardV2StateModel>, { collaboratorsInvitationModel }: InviteCollaborators) {
    return this.jobPlaningService.inviteCollaborators(collaboratorsInvitationModel).pipe(
      tap(() => {
        this.store$.dispatch(new LoadJobFileAction(collaboratorsInvitationModel.jobRefId));
        ctx.dispatch(new SetNotificationAction('Collaborators invited successfully', AlertDialogType.SUCCESS));
      }),
      catchError(error => {
        ctx.dispatch(new SetNotificationAction('Failed to invite collaborators', AlertDialogType.ERROR));
        return EMPTY;
      })
    );
  }

  @Action(RemoveCollaborator)
  removeCollaborator(ctx: StateContext<WizardV2StateModel>, { collaboratorId }: RemoveCollaborator) {
    const { jobFile } = ctx.getState();
    if (!jobFile) {
      return ctx.dispatch(new SetNotificationAction('Job file not found', AlertDialogType.ERROR));
    }

    return this.jobFileService.removeCollaborator(jobFile.orgId, jobFile.jobRefId, collaboratorId).pipe(
      tap((updatedJobFile: JobFile) => {
        const updatedJobFileCopy = { ...jobFile, collaborators: updatedJobFile.collaborators };
        ctx.patchState({ jobFile: updatedJobFileCopy });
        ctx.dispatch(new SetNotificationAction('Collaborator removed successfully', AlertDialogType.SUCCESS));
      }),
      catchError(error => {
        ctx.dispatch(new SetNotificationAction('Failed to remove collaborator', AlertDialogType.ERROR));
        return EMPTY;
      })
    );
  }

  @Action(SetRouteOptions)
  setRouteOptions(ctx: StateContext<WizardV2StateModel>, { routeOptions }: SetRouteOptions) {
    const currentState = ctx.getState();
    const currentRouteOptions = currentState.routeOptions || {
      useTimeWindows: true,
      avoidTolls: false,
      useMaxLocationCount: false,
      noOfLocations: 1,
      supplierDeliveryStartDateTime: null,
      supplierDeliveryEndDateTime: null
    };

    const updatedRouteOptions = {
      ...currentRouteOptions,
      ...routeOptions
    };

    return ctx.patchState({ routeOptions: updatedRouteOptions });
  }
}

